'use client';

import { ChevronDown } from 'lucide-react';
import React, { useId, useState } from 'react';

import { cn } from '../../utils/cn';

import type { ReactNode } from 'react';

export interface UzCollapsibleSectionProps {
  title: ReactNode;
  description?: ReactNode;
  /** Uncontrolled initial state. Default open. */
  defaultOpen?: boolean;
  /** Controlled open state (pair with onOpenChange). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional right-aligned content in the header (e.g. a count badge). */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A titled, collapsible group of content - used to fold long forms (or any
 * stacked content) into expandable sections. Dependency-free: a header button
 * toggles a region. Honours both controlled (`open`/`onOpenChange`) and
 * uncontrolled (`defaultOpen`) use.
 */
export const UzCollapsibleSection = ({
  title,
  description,
  defaultOpen = true,
  open,
  onOpenChange,
  actions,
  children,
  className,
}: UzCollapsibleSectionProps): React.JSX.Element => {
  const id = useId();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const toggle = (): void => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <section className={cn('border-input overflow-hidden rounded-md border', className)}>
      <div className="bg-muted/40 flex items-center gap-2 px-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={isOpen}
          aria-controls={id}
          className="flex min-w-0 flex-1 items-center gap-2 py-2.5 text-left text-sm font-medium outline-none"
        >
          <ChevronDown
            className={cn(
              'text-muted-foreground h-4 w-4 shrink-0 transition-transform',
              !isOpen && '-rotate-90',
            )}
          />
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{title}</span>
            {description && (
              <span className="text-muted-foreground truncate text-xs font-normal">
                {description}
              </span>
            )}
          </span>
        </button>
        {actions && <div className="flex shrink-0 items-center">{actions}</div>}
      </div>
      {isOpen && (
        <div id={id} className="min-w-0 space-y-4 p-3">
          {children}
        </div>
      )}
    </section>
  );
};
